/**
 * @file ResultsSections.tsx
 * What: Detailed Polaris tab sections for Shopify StudyRun results.
 * Transport / data-flow: Pure React render helpers fed by the Shopify results payload.
 *
 */
import type { ReactNode } from 'react'
import type { ShopifyResultsSection } from '@testfeed/shopify-contracts'
import { SectionFrame } from '../study-builder/SectionFrame'
import {
  getStudyResultDecisionRecommendation,
  formatStudyResultDecisionDetail,
  getStudyResultTabLanguage,
  getStudyResultHeadlineVerdictBandLanguage,
  productLanguage,
  shopifyComponentCopy,
  type StudyResultTabKey,
} from '@testfeed/product-language'
import { buildArmsRows, buildShopifyTreatmentIdentityMap } from '@testfeed/shopify-contracts'
import type { ShopifySelectedStudyRun } from '../../types'
import { YourQuestionsTab } from './YourQuestionsTab'
import { SegmentsTab } from './SegmentsTab'
import { ResponsesTab } from './ResponsesTab'
import type { InconclusiveResultActions } from './InconclusiveCardView'
import { TreatmentCardGrid } from './TreatmentCardGrid'
import { InfoButton, InfoPopover } from '../shared/PageHelp'

const VERDICT_INFO_ID = 'study-results-verdict-info'

const resultTabKeyById = {
  overview: 'overview',
  segments: 'segments',
  responses: 'response_themes',
  your_questions: 'your_questions',
} as const satisfies Record<string, StudyResultTabKey>

export const resultTabs = Object.entries(resultTabKeyById).map(([id, key]) => ({
  id,
  content: getStudyResultTabLanguage(key).label,
}))

/** Selects the active results section for the current Polaris tab. */
export function renderSelectedResultsTab(
  tabId: string | undefined,
  selectedRun: ShopifySelectedStudyRun,
  _setupPacketId?: string,
  inconclusiveActions?: InconclusiveResultActions,
  onSelectTreatment?: (armId: string) => void,
  focusedArmId?: string | null,
) {
  if (tabId === 'segments') return <SegmentsTab selectedRun={selectedRun} />
  if (tabId === 'responses') return <ResponsesTab selectedRun={selectedRun} onSelectTreatment={onSelectTreatment} />
  if (tabId === 'your_questions') return <YourQuestionsTab selectedRun={selectedRun} />
  return <OverviewTab selectedRun={selectedRun} inconclusiveActions={inconclusiveActions} focusedArmId={focusedArmId} />
}

function OverviewTab({
  selectedRun,
  inconclusiveActions,
  focusedArmId,
}: {
  selectedRun: ShopifySelectedStudyRun
  inconclusiveActions?: InconclusiveResultActions
  focusedArmId?: string | null
}) {
  const winnerId = selectedRun.summary.decision?.winner_arm_id
  const language = productLanguage.studyResults
  const leader = selectedRun.summary.arms.find((arm) => arm.arm_id === winnerId) || selectedRun.summary.arms[0]
  const identityByArmId = buildShopifyTreatmentIdentityMap(
    selectedRun.summary.arms.map((arm) => ({
      armId: arm.arm_id,
      isBaseline: arm.is_baseline,
    })),
    selectedRun.treatmentMode,
  )
  const leaderDisplayLabel = leader
    ? (identityByArmId.get(leader.arm_id) ?? leader.label)
    : language.overview.selectedArmFallback
  const decisionKey = selectedRun.summary.decision?.decision ?? 'single_arm'
  const runnerUp = selectedRun.summary.decision?.runner_up_arm_id
    ? selectedRun.summary.arms.find((arm) => arm.arm_id === selectedRun.summary.decision?.runner_up_arm_id)
    : null
  const runnerUpDisplayLabel = runnerUp ? (identityByArmId.get(runnerUp.arm_id) ?? runnerUp.label) : null
  const decisionDetail = formatStudyResultDecisionDetail(decisionKey, leaderDisplayLabel, runnerUpDisplayLabel)
  const verdictLanguage = getStudyResultHeadlineVerdictBandLanguage(decisionKey, leader?.verdict_band)
  const recommendationText = getStudyResultDecisionRecommendation(
    decisionKey,
    leader?.verdict,
    selectedRun.summary.decision?.recommended_action,
  )
  const preview = selectedRun.previewContext ?? selectedRun.changeList?.previewContext
  const rows = selectedRun.details?.arms ? buildArmsRows(selectedRun.details.arms, selectedRun.treatmentMode) : null

  return (
    <s-stack direction="block" gap="large-100">
      <ResultOutcomeView
        header={{
          title: language.overview.outcomeHeading,
          description: language.overview.outcomeDescription,
          badge: {
            text: verdictLanguage.label,
            tone:
              verdictLanguage.tone === 'positive' ? 'success' : verdictLanguage.tone === 'caution' ? 'warning' : 'info',
          },
          help: {
            heading: shopifyComponentCopy.resultsShopifyStudyResultsPage.verdictInfoHeading,
            why: shopifyComponentCopy.resultsShopifyStudyResultsPage.verdictInfoWhy,
            how: shopifyComponentCopy.resultsShopifyStudyResultsPage.verdictInfoHow,
          },
        }}
      >
        <s-text>{recommendationText}</s-text>
        <s-text color="subdued">{decisionDetail}</s-text>
      </ResultOutcomeView>

      <TreatmentCardGrid
        rows={rows}
        productTitle={preview?.title}
        productDescription={preview?.descriptionHtml}
        displayPrice={preview?.displayPrice}
        treatmentField={selectedRun.treatmentField}
        focusedArmId={focusedArmId}
        onReviewReactions={
          inconclusiveActions?.onReviewReactions ? () => inconclusiveActions.onReviewReactions?.() : undefined
        }
        onTrySomething={inconclusiveActions?.onCreateNewVersion}
      />
    </s-stack>
  )
}

/** Existing outcome help controls and shared section frame; the server supplies badge/copy and meaning. */
export function ResultOutcomeView({
  header,
  children,
}: {
  header: ShopifyResultsSection['header']
  children: ReactNode
}) {
  const help = header.help
  const action = help ? <InfoButton id={VERDICT_INFO_ID} heading={help.heading} /> : null
  const popover = help ? <InfoPopover id={VERDICT_INFO_ID} why={help.why} how={help.how} /> : null
  return (
    <>
      <SectionFrame title={header.title} description={header.description} badge={header.badge} action={action}>
        {children}
      </SectionFrame>
      {popover}
    </>
  )
}
